import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { ingredientColumns, doughColumns, userColumns, productColumns, lineColumns, productionListColumns, ovenColumns } from "../../datatablesource";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const Datatable = ({ title, type }) => {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const organizationId = JSON.parse(localStorage.getItem("organizations")).selected.id;

  const columnsConfig = {
    ingredients: {
      columns: ingredientColumns,
      defaultSort: [{ field: "manufacturerCode", sort: "asc" }]
    },
    doughs: {
      columns: doughColumns,
      defaultSort: [{ field: "manufacturerCode", sort: "asc" }]
    },
    products: {
      columns: productColumns,
      defaultSort: [{ field: "name", sort: "asc" }]
    },
    users: { columns: userColumns },
    lines: {
      columns: lineColumns,
      defaultSort: [{ field: "name", sort: "asc" }]
    },
    ovens: {
      columns: ovenColumns,
      defaultSort: [{ field: "name", sort: "asc" }]
    },
    productionlists: { columns: productionListColumns },
  };

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "organizations", organizationId, type),
      (snapShot) => {
        let list = [];
        snapShot.docs.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        setData(list);
      },
      (error) => {
        console.log(error);
      }
    );

    return () => {
      unsub();
    };
  }, [type, organizationId]);

  const updateReferences = async (deletedId, deletedType) => {
    const collections = ['ingredients', 'doughs', 'products', 'ovens'];
    const deletedRef = `${deletedType}/${deletedId}`;

    for (const col of collections) {
      const q = collection(db, "organizations", organizationId, col);
      const snapshot = await getDocs(q);

      snapshot.forEach(async (document) => {
        const docRef = doc(db, "organizations", organizationId, col, document.id);
        const data = document.data();

        const ingredientList = Array.isArray(data.ingredients) ? data.ingredients : [];
        const updatedIngredients = ingredientList.filter(ingredientMap => {
          const ingredientId = Object.keys(ingredientMap)[0];
          return ingredientId !== deletedRef;
        });

        if (updatedIngredients.length !== ingredientList.length) {
          await updateDoc(docRef, { ingredients: updatedIngredients });
        }
      });
    }
    updateProductionLines(deletedId, deletedType);
    updateProductionLists(deletedId, deletedType);
  };

  const updateProductionLines = async (deletedId, deletedType) => {
    const collections = ['lines'];
    const deletedRef = `${deletedType}/${deletedId}`;

    for (const col of collections) {
      const q = collection(db, "organizations", organizationId, col);
      const snapshot = await getDocs(q);

      snapshot.forEach(async (document) => {
        const docRef = doc(db, "organizations", organizationId, col, document.id);
        const data = document.data();

        const productList = Array.isArray(data.products) ? data.products : [];
        const updatedProducts = productList.filter(ingredientMap => {
          const productId = Object.keys(ingredientMap)[0];
          return productId !== deletedRef;
        });

        if (updatedProducts.length !== productList.length) {
          await updateDoc(docRef, { products: updatedProducts });
        }
      });
    }
  };

  const updateProductionLists = async (deletedId, deletedType) => {
    const collections = ['productionlists'];
    const deletedRef = `${deletedType}/${deletedId}`;

    for (const col of collections) {
      const q = collection(db, "organizations", organizationId, col);
      const snapshot = await getDocs(q);

      snapshot.forEach(async (document) => {
        const docRef = doc(db, "organizations", organizationId, col, document.id);
        const data = document.data();

        const linesAndProducts = Array.isArray(data.linesAndProducts) ? data.linesAndProducts : [];
        const updatedLinesAndProducts = linesAndProducts.filter(linesAndProductMap => {
          const lineId = Object.keys(linesAndProductMap)[0];
          return lineId !== deletedRef;
        });

        if (updatedLinesAndProducts.length !== linesAndProducts.length) {
          await updateDoc(docRef, { linesAndProducts: updatedLinesAndProducts });
        } else {
          const updatedLinesAndProductsMapped = linesAndProducts.map(linesAndProductsMap => {
            const lineId = Object.keys(linesAndProductsMap)[0];
            const products = linesAndProductsMap[lineId].filter(productId => productId !== deletedRef);

            if (products.length !== linesAndProductsMap[lineId].length) {
              return { [lineId]: products };
            }
            return linesAndProductsMap;
          });

          if (updatedLinesAndProductsMapped.some(map => Object.keys(map)[0] !== deletedRef)) {
            await updateDoc(docRef, { linesAndProducts: updatedLinesAndProductsMapped });
          }
        }
      });
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this item?");
    if (confirmDelete) {
      try {
        await updateReferences(id, type);
        await deleteDoc(doc(db, "organizations", organizationId, type, id));
        setData(data.filter((item) => item.id !== id));
      } catch (err) {
        console.log(err);
      }
    }
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => (
        <div className="cellAction">
          <Link to={`/${type}/${params.row.id}`} style={{ textDecoration: "none" }}>
            <div className="viewButton">View</div>
          </Link>
          <div
            className="deleteButton"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </div>
        </div>
      ),
    },
  ];

  const filteredData = data.filter((item) => {
    const query = searchQuery.toLowerCase();
    const name = item.name ? item.name.toLowerCase() : "";
    const manufacturerCode = item.manufacturerCode ? item.manufacturerCode.toLowerCase() : "";
    return name.includes(query) || manufacturerCode.includes(query);
  });

  return (
    <div className="datatable">
      <div className="datatableTitle">
        {title.charAt(0).toUpperCase() + title.slice(1)}

        <div className="datatableSearch">
          <input
            type="text"
            placeholder="Search by name or manufacturer code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Link to={`/${type}/new`} className="link">
          Add New
        </Link>
      </div>


      <DataGrid
        key={type}
        className="datagrid"
        rows={filteredData}
        columns={columnsConfig[type].columns.concat(actionColumn)}
        pageSize={9}
        rowsPerPageOptions={[9]}
        checkboxSelection
        initialState={{
          sorting: {
            sortModel: columnsConfig[type].defaultSort,
          },
        }}
      />
    </div>
  );
};

export default Datatable;
